import { useMyAccess } from '@/lib/useMyAccess';
import { Route, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GroupedLayout } from '@/components/layout/GroupedLayout';
import { api } from '@/lib/api';
import { deskGroups } from './nav';
import { AdmissionsPage as FullAdmissionsPage } from '@/features/owner/pages/AdmissionsPage';
import { QuestionBanksPage } from '@/features/owner/pages/QuestionBanksPage';
import { ResultsPage as ClassResultsPage } from '@/features/owner/pages/ResultsPage';
import { OwnerCbtExamsListPage } from '@/features/owner/pages/OwnerCbtExamsListPage';
import { OwnerCbtCreatePage } from '@/features/owner/pages/OwnerCbtCreatePage';
import { OwnerCbtExamDetailPage } from '@/features/owner/pages/OwnerCbtExamDetailPage';
import { LoginsPage } from '@/features/shared/people/LoginsPage';
import { ScoreLocksPage } from '@/features/shared/results/ScoreLocksPage';

import { StudentsPage } from '@/features/owner/pages/StudentsPage';
import { StudentProfilePage } from '@/features/owner/pages/StudentProfilePage';
import { PromotionPage } from '@/features/owner/pages/PromotionPage';
import { StaffPage } from '@/features/owner/pages/StaffPage';
import { SubjectRegistrationPage } from '@/features/owner/pages/SubjectRegistrationPage';
import { BroadsheetPage } from '@/features/owner/pages/BroadsheetPage';
import { PublishPage } from '@/features/owner/pages/PublishPage';
import { NoticesPage } from '@/features/owner/pages/NoticesPage';
import { CalendarPage } from '@/features/owner/pages/CalendarPage';
import { ScoreEntryPage } from '@/features/owner/pages/ScoreEntryPage';
import { OwnerImportScoresPage } from '@/features/owner/pages/OwnerImportScoresPage';
import { SchoolReportsPage } from '@/features/shared/reports/SchoolReports';
import { StaffAttendancePage } from '@/features/shared/people/StaffAttendancePage';
import { AlumniPage } from './pages/AlumniPage';
import { DashboardPage } from './pages/DashboardPage';
import { BulkImportPage } from './pages/BulkImportPage';
import { ResultApprovalPage } from './pages/ResultApprovalPage';
import { TranscriptRequestsPage } from './pages/TranscriptRequestsPage';
import { AttendancePage } from './pages/AttendancePage';
import { ClassesPage } from './pages/ClassesPage';
import { HeldResultsPage } from '@/features/shared/results/HeldResultsPage';
import { StaffMessagesPage } from '@/features/shared/support/StaffMessagesPage';
import { SupportCenter } from '@/features/shared/support/SupportCenter';
import { LessonNotesPage } from '@/features/shared/teaching/LessonNotesPage';
import { LessonsStudio } from '@/features/shared/teaching/LessonsStudio';
import { LiveClassesPage } from '@/features/shared/teaching/LiveClassesPage';
import { RemarksPage } from '@/features/owner/pages/RemarksPage';
import { FamiliesPage } from '@/features/owner/pages/FamiliesPage';
import { TimetablePage } from '@/features/owner/pages/TimetablePage';
import { BirthdaysPage } from '@/features/owner/pages/BirthdaysPage';
import { ClockInPage as MyClockInPage } from '@/features/teacher/pages/ClockInPage';
import { MyPayPage } from '@/features/teacher/pages/MyPayPage';
import { AccountPage } from '@/features/auth/pages/AccountPage';
import { NotificationsCenter } from '@/features/auth/pages/NotificationsCenter';

export function DeskLayout() {
  // live counts on the menu: what is waiting on the desk
  const d = useQuery({ queryKey: ['owner-dashboard'], queryFn: () => api.get('/school/dashboard').then((r) => r.data?.data), refetchInterval: 120_000, retry: false });
  const p = d.data?.pending ?? {};
  // pages the school owner switched off for desk officers
  const { hidden, customRole } = useMyAccess();
  return (
    <GroupedLayout base="/desk" roleLabel={customRole ?? "Desk Officer"} groups={deskGroups} searchTo="students" padded hidden={hidden}
      ownPadding={['students', 'student', 'staff', 'staff-attendance', 'clock-in-qr', 'subject-registration', 'promotion', 'broadsheet', 'publish-results', 'notices', 'calendar', 'attendance', 'score-entry', 'import-scores', 'reports', 'admissions', 'question-banks', 'logins', 'score-locks', 'class-results', 'cbt-exams', 'families', 'timetable', 'remarks', 'birthdays', 'held-results', 'lessons', 'live-classes', 'lesson-notes', 'messages', 'support']}
      badges={{ admissions: p.applications, families: p.parent_link_requests, 'held-results': p.held_results, 'transcript-requests': p.document_requests, 'lesson-notes': p.lesson_notes, support: p.support_replies }} />
  );
}

export const deskRoutes = () => [
  // ── Overview ──
  <Route key="dashboard" path="dashboard" element={<DashboardPage />} />,

  // ── Admissions ──
  // Both routes land on AdmissionsPage — it has a tabbed interface for
  // applications list, cycles, and entrance exams. Using AdmissionsPage
  // for /applications replaces the old stub DeskApplicationsPage which
  // used stale field names (a.class_applied, a.parent_phone) that don't
  // match the backend shape.
  <Route key="admissions" path="admissions" element={<FullAdmissionsPage />} />,
  <Route key="question-banks" path="question-banks" element={<QuestionBanksPage />} />,
  <Route key="logins" path="logins" element={<LoginsPage />} />,
  <Route key="score-locks" path="score-locks" element={<ScoreLocksPage />} />,
  <Route key="class-results" path="class-results" element={<ClassResultsPage />} />,
  <Route key="cbt-exams" path="cbt-exams" element={<OwnerCbtExamsListPage />} />,
  <Route key="cbt-new" path="cbt-exams/new" element={<OwnerCbtCreatePage />} />,
  <Route key="cbt-one" path="cbt-exams/:id" element={<OwnerCbtExamDetailPage />} />,
  <Route key="apps" path="applications" element={<Navigate to="/desk/admissions" replace />} />,
  <Route key="admit" path="admit-exam-decide" element={<Navigate to="/desk/admissions" replace />} />,

  // ── Students ──
  <Route key="students" path="students" element={<StudentsPage />} />,
  <Route key="students-one" path="students/:id" element={<StudentProfilePage />} />,
  <Route key="directory" path="directory" element={<Navigate to="/desk/students" replace />} />,
  <Route key="student-profile" path="student-profile" element={<Navigate to="/desk/students" replace />} />,
  <Route key="profile" path="student/:id" element={<StudentProfilePage />} />,
  <Route key="families" path="families" element={<FamiliesPage />} />,
  <Route key="parents" path="parents" element={<Navigate to="/desk/families" replace />} />,
  <Route key="parents-families" path="parents-families" element={<Navigate to="/desk/families" replace />} />,
  <Route key="guardians" path="guardians" element={<Navigate to="/desk/families" replace />} />,
  <Route key="parent-links" path="parent-links" element={<Navigate to="/desk/families" replace />} />,
  <Route key="timetable" path="timetable" element={<TimetablePage />} />,
  <Route key="staff-attendance" path="staff-attendance" element={<StaffAttendancePage />} />,
  <Route key="clock-in-qr" path="clock-in-qr" element={<Navigate to="/desk/staff-attendance" replace />} />,
  <Route key="staff" path="staff" element={<StaffPage />} />,
  <Route key="remarks" path="remarks" element={<RemarksPage />} />,
  <Route key="live-classes" path="live-classes" element={<LiveClassesPage />} />,
  <Route key="birthdays" path="birthdays" element={<BirthdaysPage />} />,
  <Route key="clock-in" path="clock-in" element={<MyClockInPage />} />,
  <Route key="my-pay" path="my-pay" element={<MyPayPage />} />,
  <Route key="notifications" path="notifications" element={<NotificationsCenter />} />,
  <Route key="account" path="account" element={<AccountPage />} />,
  <Route key="promotion" path="promotion" element={<PromotionPage />} />,
  <Route key="alumni" path="graduation-alumni" element={<AlumniPage />} />,
  <Route key="attendance" path="attendance" element={<AttendancePage />} />,
  <Route key="subject-registration" path="subject-registration" element={<SubjectRegistrationPage />} />,
  <Route key="bulk-import" path="bulk-import" element={<BulkImportPage />} />,

  // ── Results Support ──
  <Route key="result-approval" path="result-approval" element={<ResultApprovalPage />} />,
  <Route key="broadsheet" path="broadsheet" element={<BroadsheetPage />} />,
  <Route key="publish-results" path="publish-results" element={<PublishPage />} />,
  <Route key="score-entry" path="score-entry" element={<ScoreEntryPage />} />,
  <Route key="import-scores" path="import-scores" element={<OwnerImportScoresPage />} />,
  <Route key="held-results" path="held-results" element={<HeldResultsPage />} />,
  <Route key="messages" path="messages" element={<StaffMessagesPage />} />,
  <Route key="support" path="support" element={<SupportCenter />} />,
  <Route key="lesson-notes" path="lesson-notes" element={<LessonNotesPage />} />,
  <Route key="lessons" path="lessons" element={<LessonsStudio />} />,
  <Route key="transcript-requests" path="transcript-requests" element={<TranscriptRequestsPage />} />,

  // ── Communication ──
  <Route key="notices" path="notices" element={<NoticesPage />} />,
  <Route key="critical-alerts" path="critical-alerts" element={<Navigate to="/desk/notices" replace />} />,

  // Legacy root → dashboard
  <Route index element={<Navigate to="dashboard" replace />} />,
  <Route key="classes" path="classes" element={<ClassesPage />} />,
  <Route key="calendar" path="calendar" element={<CalendarPage />} />,
  <Route key="reports" path="reports" element={<div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6"><SchoolReportsPage /></div>} />,
];
